import { ReactElement, useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl, FormControlLabel,
  InputLabel, MenuItem,
  Switch, Tab, Tabs, TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  INotificationSubscription,
  IOptionMenu,
  ISchemaNode,
  ITelegramBotConfig,
  IUser,
  IUserConfig,
  languageType,
  NullValue
} from "../../types";
import { SystemContext } from "../../context/systemContext";
import { addAlert } from "../../actions";
import { useAsyncFetch, useFetch } from "../../system/useFetch";
import { useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import Select  from "@mui/material/Select";
import TabPanel from "../tabPanel";
import qrImage from "../../assets/qrcode-bot.png";
import { cloneObject } from "../../system/utils";
import NotificationPanel from "./panels/notificationPanel";

interface IProps {
  user: IUser;
  open: boolean;
  closed: () => void;
}

const formControlSmall = {
  m: 1,
  minWidth: 150,
  width: 150,
};

const getMenuOptions = (factory: ISchemaNode | null, area: ISchemaNode | null, zone: ISchemaNode | null): IOptionMenu[] => {
  if (zone) {
    return zone.menu;
  }

  if (area) {
    return area.menu;
  }

  if(factory) {
    return factory.menu;
  }

  return [];
};

const ProfileDialog = ({ open, closed, user }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const [ dateFormat, setDateFormat ] = useState(user.config.locale.dateFormat);
  const [ timeFormat, setTimeFormat ] = useState(user.config.locale.timeFormat);
  const [ language, setLanguage ] = useState<languageType>(user.config.locale.language);
  const [ darkMode, setDarkModeEdit ] = useState<boolean>(user.config.darkMode);
  const [ devMode, setDevModeEdit ] = useState<boolean>(user.config.custom.devMode ?? false);
  const [tab, setTab] = useState(0);

  const [ factorySelected, setFactorySelected ] = useState<ISchemaNode | null>(null);
  const [ areaSelected, setAreaSelected ] = useState<ISchemaNode | null>(null);
  const [ zoneSelected, setZoneSelected ] = useState<ISchemaNode | null>(null);
  const [ optionMenu, setOptionMenu ] = useState<IOptionMenu | null>(null);
  const [ optionMenus, setOptionMenus ] = useState<IOptionMenu[]>([]);
  const [ telegramBotConfig, setTelegramBotConfig ] = useState<ITelegramBotConfig>(cloneObject(user.config.custom.telegram));
  const [ subscriptions, setSubscriptions ] = useState<INotificationSubscription[]>([]);
  const { data } = useFetch<INotificationSubscription[]>(
    {
      module: "notification",
      query: "getUserSubscriptions",
      payload: {}
    });

  const [ executeCommand ] = useAsyncFetch();

  const organization = systemContext.schema;

  useEffect(() => {
    const defaultNode = user.config.custom.defaultNode;
    const factory = defaultNode ? systemContext.schema.find(x => x.nodeID === defaultNode.factory) : null;
    const area  = factory ? factory.children?.find(x => x.nodeID === defaultNode?.area) : null;
    const zone = area ? area.children?.find(x => x.nodeID === defaultNode?.zone) : null;
    const menus = getMenuOptions(factory ?? null, area ?? null, zone ?? null);
    const menuSelected = menus.find(x => x.title === defaultNode?.view);

    setFactorySelected(factory ?? null);
    setAreaSelected(area ?? null);
    setZoneSelected(zone ?? null);
    setOptionMenus(menus);
    setOptionMenu(menuSelected ?? null);
  }, [ user ]);

  useEffect(() => {
    if (data) {
      setSubscriptions(data);
    }
  }, [data]);

  const onFactoryChanged = (nodeName: string) => {
    setFactorySelected(organization.find(n => n.name === nodeName) ?? null);
    setAreaSelected(null);
    setZoneSelected(null);
    setOptionMenu(null);
  };

  const onAreaChanged = (nodeName: string) => {
    if (!factorySelected) {
      return;
    }

    const areaNode = factorySelected.children?.find(n => n.name === nodeName) ?? null;
    setAreaSelected(areaNode);
    setZoneSelected(null);
    setOptionMenu(null);
    setOptionMenus(getMenuOptions(factorySelected, areaNode, null));
  };

  const onZoneChanged = (nodeName: string) => {
    if (!areaSelected) {
      return;
    }

    const zoneNode = areaSelected.children?.find(n => n.name === nodeName) ?? null;
    setZoneSelected(zoneNode);
    setOptionMenu(null);
    setOptionMenus(getMenuOptions(factorySelected, areaSelected, zoneNode));
  };


  const onMenuChanged = (menuName: string) => {
    if (!zoneSelected) {
      return;
    }

    setOptionMenu(optionMenus.find(n => n.title === menuName) ?? null);
  };

  const onUpdate = () => {
    if (tab === 2) {
      // Notifications
      executeCommand({
        module: "notification",
        query: "updateSubscriptions",
        payload: {
          subscriptions
        }
      }).then(() => {
        dispatch(addAlert({ message: "The user subscriptions were saved!", severity: "success", hash: undefined }));
      });

      return;
    }

    const config: IUserConfig = {
      locale: {
        ... user.config.locale,
        dateFormat,
        timeFormat,
        language
      },
      darkMode,
      custom: {
        ...user.config.custom,
        devMode,
        defaultNode: {
          factory: factorySelected ? factorySelected.nodeID : null,
          area: areaSelected ? areaSelected.nodeID : null,
          zone: zoneSelected ? zoneSelected.nodeID : null,
          view: optionMenu ? optionMenu.title : null
        },
        telegram: telegramBotConfig
      }
    };

    executeCommand({
      module: "security",
      query: "updateUserConfig",
      payload: config
    }).then(() => {
      systemContext.updateUserConfig(config);
      dispatch(addAlert({ message: "The user config was saved!", severity: "success", hash: undefined }));

      if (tab === 0){
        closed();
      }
    });
  };

  const handleTabChange = (_event: any, newValue: number) => {
    setTab(newValue);
  };

  const onTelegramChanged = (field: keyof ITelegramBotConfig, value: any) => {
    setTelegramBotConfig({ ...telegramBotConfig, [field]: value });
  };

  const updateSubscriptions = (item: INotificationSubscription) => {
    if (subscriptions.find(s => s.topicID === item.topicID)) {
      setSubscriptions(subscriptions.map(s => s.topicID === item.topicID ? item : s));
    } else {
      setSubscriptions([...subscriptions, item]);
    }
  };

  const isDev = user.permissions.find(p => p.code === "DEV") !== undefined;

  return (
    <Dialog onClose={closed} aria-labelledby="customized-dialog-title" open={open} maxWidth={"lg"}>

      <DialogTitle id="customized-dialog-title">
        {`Profile - ${user?.fullName}`}
      </DialogTitle>

      <DialogContent dividers={true} style={{ width: 800 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab label="General"/>
          <Tab label="Telegram Bot"/>
          <Tab label="Notifications"/>
        </Tabs>

        <TabPanel value={tab} index={0}>
          <>
            <div className="display-flex-row align-items-center m-2">
              <FormControl sx={formControlSmall}>
                <InputLabel>Date Format</InputLabel>
                <Select
                  variant="standard"
                  value={dateFormat}
                  displayEmpty={true}
                  onChange={(event) => setDateFormat(event.target.value as string)}>
                  <MenuItem value="MM-dd-yyyy">MM-dd-yyyy</MenuItem>
                  <MenuItem value="dd-MM-yyyy">dd-MM-yyyy</MenuItem>
                  <MenuItem value="yyyy-MM-dd">yyyy-MM-dd</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={formControlSmall}>
                <InputLabel>Time Format</InputLabel>
                <Select
                  variant="standard"
                  value={timeFormat}
                  displayEmpty={true}
                  onChange={(event) => setTimeFormat(event.target.value as string)}>
                  <MenuItem value="HH:mm:ss">HH:mm:ss</MenuItem>
                  <MenuItem value="hh:mm:ss a">hh:mm:ss am/pm</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={formControlSmall}>
                <InputLabel>Language</InputLabel>
                <Select
                  variant="standard"
                  value={language}
                  displayEmpty={true}
                  onChange={(event) => setLanguage(event.target.value as languageType)}>
                  <MenuItem value="en-us">en-us</MenuItem>
                  <MenuItem value="es-mx">es-mx</MenuItem>
                  <MenuItem value="pt-br">pt-br</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel name="DarkMode" label="Dark Mode"
                control={<Switch checked={darkMode} onChange={(event) => setDarkModeEdit(event.target.checked)} />} />


              {isDev &&
                 <FormControlLabel name="DevMode" label="Dev Mode"
                   control={<Switch checked={devMode} onChange={(event) => setDevModeEdit(event.target.checked)}/>}/>}
            </div>

            <div className="display-flex-row align-items-center m-2">
              <FormControl sx={formControlSmall}>
                <InputLabel>Factory</InputLabel>
                <Select
                  variant="standard"
                  value={factorySelected ? factorySelected.name : NullValue}
                  displayEmpty={true}
                  onChange={(event) => onFactoryChanged(event.target.value as string)}>
                  <MenuItem value={NullValue}>None</MenuItem>
                  { organization.map((node, index) => {
                    return (
                      <MenuItem key={`select-factory-${index}`} value={node.name}>{node.name}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl sx={formControlSmall}>
                <InputLabel>Area</InputLabel>
                <Select
                  variant="standard"
                  value={areaSelected ? areaSelected.name : NullValue}
                  displayEmpty={true}
                  onChange={(event) => onAreaChanged(event.target.value as string)}>
                  <MenuItem value={NullValue}>None</MenuItem>
                  { factorySelected && factorySelected.children && factorySelected.children.map((node, index) => {
                    return (
                      <MenuItem key={`select-area-${index}`} value={node.name}>{node.name}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl sx={formControlSmall}>
                <InputLabel>Zone</InputLabel>
                <Select
                  variant="standard"
                  value={zoneSelected ? zoneSelected.name : NullValue}
                  displayEmpty={true}
                  onChange={(event) => onZoneChanged(event.target.value as string)}>
                  <MenuItem value={NullValue}>None</MenuItem>
                  { areaSelected && areaSelected.children && areaSelected.children.map((node, index) => {
                    return (
                      <MenuItem key={`select-zone-${index}`} value={node.name}>{node.name}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl sx={formControlSmall}>
                <InputLabel>View</InputLabel>
                <Select
                  variant="standard"
                  value={optionMenu ? optionMenu.title : NullValue}
                  displayEmpty={true}
                  onChange={(event) => onMenuChanged(event.target.value as string)}>
                  <MenuItem value={NullValue}>None</MenuItem>
                  { optionMenus.map((node, index) => {
                    return (
                      <MenuItem key={`select-menu-${index}`} value={node.title}>{node.title}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <>
            <div className="display-flex-row align-items-center m-2">
              <TextField
                variant="standard"
                sx={{ margin: 1, minWidth: 284, width: 284 }}
                placeholder={"Username"}
                label={"Username"}
                value={telegramBotConfig.username}
                onChange={(e) => onTelegramChanged("username", e.target.value)}/>

              <TextField
                variant="standard"
                sx={{ margin: 1, minWidth: 284, width: 284 }}
                placeholder={"Phone"}
                label={"Phone"}
                value={telegramBotConfig.phone}
                onChange={(e) => onTelegramChanged("phone", e.target.value)}/>

              <FormControlLabel
                control={<Switch checked={telegramBotConfig.enabled} onChange={() => onTelegramChanged("enabled", !telegramBotConfig.enabled)} />}
                label="Enabled" />
            </div>
            <div className="display-flex-column align-items-center m-2">
              <img src={qrImage} alt={"telegram-bot"} style={{ height: "120px", width: "120px" }}/>
              <small>Telegram Link</small>
            </div>
          </>
        </TabPanel>

        <TabPanel index={tab} value={2}>
          <NotificationPanel subscriptions={subscriptions} updateSubscriptions={updateSubscriptions}  />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onUpdate} variant="contained" color="primary" startIcon={<SaveIcon />}>
          Update
        </Button>
        <Button onClick={closed} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileDialog;
